import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { Check, Loader2, LayoutTemplate, CreditCard, Save, Eye, EyeOff, Mail, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingAndPayments() {
  const { authFetch } = useAdmin();
  const [landing, setLanding] = useState(null);
  const [savingLanding, setSavingLanding] = useState(false);

  const [mypos, setMypos] = useState({
    sid: '', client_id: '', client_secret: '', wallet_number: '',
    private_key_pem: '', public_cert_pem: '',
    private_key_present: false, public_cert_present: false,
    private_key_fingerprint: '', public_cert_fingerprint: '',
    mode: 'sandbox', currency: 'EUR', configured: false,
  });
  const [savingMypos, setSavingMypos] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const [email, setEmail] = useState({
    api_key: '', api_key_masked: '', api_key_configured: false,
    from_email: '', from_name: '', mock_mode: false,
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');

  useEffect(() => {
    authFetch(`${API}/admin/landing`).then(r => r.json()).then(setLanding).catch(() => {});
    authFetch(`${API}/admin/payments/mypos/keys`).then(r => r.json()).then(d => setMypos(m => ({ ...m, ...d }))).catch(() => {});
    authFetch(`${API}/admin/email/keys`).then(r => r.json()).then(d => setEmail(e => ({ ...e, ...d }))).catch(() => {});
     
  }, [authFetch]);

  async function selectVariant(variant) {
    setSavingLanding(true);
    try {
      const r = await authFetch(`${API}/admin/landing`, { method: 'PUT', body: JSON.stringify({ variant }) });
      if (!r.ok) throw new Error('fail');
      const d = await r.json();
      setLanding(d);
      toast.success(`Layout: ${d.variants[d.variant].label}`);
    } catch { toast.error('Greška pri spremanju layouta'); } finally { setSavingLanding(false); }
  }

  async function saveMypos(e) {
    e.preventDefault();
    setSavingMypos(true);
    try {
      const payload = {
        sid: mypos.sid, client_id: mypos.client_id,
        client_secret: mypos.client_secret?.includes('•') ? '' : (mypos.client_secret || ''),
        wallet_number: mypos.wallet_number,
        private_key_pem: mypos.private_key_pem || '',
        public_cert_pem: mypos.public_cert_pem || '',
        mode: mypos.mode, currency: mypos.currency || 'EUR',
      };
      const r = await authFetch(`${API}/admin/payments/mypos/keys`, { method: 'PUT', body: JSON.stringify(payload) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'fail');
      toast.success('MyPOS ključevi spremljeni');
      const refreshed = await authFetch(`${API}/admin/payments/mypos/keys`).then(rr => rr.json());
      setMypos(m => ({ ...m, ...refreshed, private_key_pem: '', public_cert_pem: '' }));
    } catch (err) {
      toast.error(`Greška: ${err.message}`);
    } finally { setSavingMypos(false); }
  }

  async function saveEmail(e) {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const payload = {
        api_key: email.api_key?.includes('•') ? '' : (email.api_key || ''),
        from_email: email.from_email, from_name: email.from_name,
        mock_mode: !!email.mock_mode,
      };
      const r = await authFetch(`${API}/admin/email/keys`, { method: 'PUT', body: JSON.stringify(payload) });
      if (!r.ok) throw new Error('fail');
      toast.success(payload.mock_mode ? 'Email u mock načinu — slat će se samo u log' : 'SendGrid konfiguracija spremljena');
      const refreshed = await authFetch(`${API}/admin/email/keys`).then(rr => rr.json());
      setEmail(x => ({ ...x, ...refreshed, api_key: '' }));
    } catch { toast.error('Greška'); } finally { setSavingEmail(false); }
  }

  async function sendTestEmail() {
    if (!testRecipient) { toast.error('Unesite primatelja'); return; }
    try {
      const r = await authFetch(`${API}/admin/email/test`, { method: 'POST', body: JSON.stringify({ to: testRecipient }) });
      const d = await r.json();
      if (d.ok && d.mocked) toast.success(`Mock email zapisan u log (id=${d.id})`);
      else if (d.ok) toast.success(`Poslano preko SendGrida (status ${d.status_code})`);
      else toast.error(d.error || 'Neuspjelo slanje');
    } catch { toast.error('Greška pri slanju'); }
  }

  return (
    <div className="p-6 space-y-10 max-w-5xl" data-testid="landing-payments-page">
      <header>
        <h1 className="text-2xl font-semibold">Naslovnica, MyPOS i Email</h1>
        <p className="text-xs text-zinc-400 mt-1">Odaberite layout naslovnice i upravljajte MyPOS / SendGrid API ključevima.</p>
      </header>

      {/* Landing variants */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <LayoutTemplate size={18} className="text-red-500" />
          <h2 className="text-lg font-bold">Layout naslovnice</h2>
        </div>
        {!landing ? (
          <div className="text-zinc-500 text-sm">Učitavam…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {Object.values(landing.variants).map(v => {
              const active = landing.variant === v.key;
              return (
                <button key={v.key} type="button" disabled={savingLanding}
                        onClick={() => !active && selectVariant(v.key)}
                        data-testid={`landing-variant-${v.key}`}
                        className={`text-left rounded-xl border-2 p-5 transition-all ${active ? 'border-red-500 bg-red-500/5' : 'border-zinc-800 hover:border-zinc-600 bg-zinc-900/40'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-base font-bold flex items-center gap-2">
                        {v.label}
                        {active && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-2 py-0.5 rounded-sm"><Check size={11}/> Aktivno</span>}
                      </div>
                      <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{v.description}</p>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {v.category_layouts.map(l => (
                          <span key={l} className="text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-800 px-2 py-0.5 rounded">{l}</span>
                        ))}
                      </div>
                    </div>
                    {savingLanding && !active && <Loader2 size={16} className="animate-spin text-zinc-500" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* MyPOS keys */}
      <section className="space-y-4 pt-6 border-t border-zinc-800">
        <div className="flex items-center gap-2">
          <CreditCard size={18} className="text-red-500" />
          <h2 className="text-lg font-bold">MyPOS IPC integracija</h2>
          {mypos.configured && <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-sm">Konfigurirano</span>}
        </div>
        <p className="text-xs text-zinc-400 -mt-1">Unesite SID, wallet broj i RSA ključeve iz MyPOS panela. Koristi se za napojnice i pretplate.</p>
        <form onSubmit={saveMypos} className="space-y-4 max-w-2xl">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Način rada</label>
              <select value={mypos.mode} onChange={e => setMypos({ ...mypos, mode: e.target.value })}
                      data-testid="mypos-mode"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm">
                <option value="sandbox">Sandbox (testiranje)</option>
                <option value="production">Produkcija</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Valuta</label>
              <select value={mypos.currency || 'EUR'} onChange={e => setMypos({ ...mypos, currency: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm">
                <option value="EUR">EUR</option>
                <option value="HRK">HRK</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">SID (ID trgovca)</label>
              <input value={mypos.sid} onChange={e => setMypos({ ...mypos, sid: e.target.value })}
                     data-testid="mypos-sid"
                     className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Broj novčanika (Wallet Number)</label>
              <input value={mypos.wallet_number} onChange={e => setMypos({ ...mypos, wallet_number: e.target.value })}
                     data-testid="mypos-wallet"
                     className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1">Client ID (opcionalno)</label>
            <input value={mypos.client_id} onChange={e => setMypos({ ...mypos, client_id: e.target.value })}
                   data-testid="mypos-client-id"
                   className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1">Client Secret (opcionalno)</label>
            <div className="relative">
              <input type={showSecret ? 'text' : 'password'}
                     value={mypos.client_secret || ''}
                     onChange={e => setMypos({ ...mypos, client_secret: e.target.value })}
                     data-testid="mypos-client-secret"
                     placeholder={mypos.configured ? '(ostavite prazno za zadržavanje postojećeg)' : ''}
                     className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 pr-10 text-sm" />
              <button type="button" onClick={() => setShowSecret(v => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 p-1">
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1 flex items-center gap-2">
              Privatni ključ (PEM)
              {mypos.private_key_present && (
                <span className="text-[10px] text-emerald-400">✓ Učitan · fp {mypos.private_key_fingerprint}</span>
              )}
            </label>
            <textarea rows={6}
                      value={mypos.private_key_pem || ''}
                      onChange={e => setMypos({ ...mypos, private_key_pem: e.target.value })}
                      placeholder={mypos.private_key_present ? '(ostavite prazno za zadržavanje postojećeg)' : '-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
                      data-testid="mypos-private-key"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-xs font-mono" />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1 flex items-center gap-2">
              Javni certifikat (PEM)
              {mypos.public_cert_present && (
                <span className="text-[10px] text-emerald-400">✓ Učitan · fp {mypos.public_cert_fingerprint}</span>
              )}
            </label>
            <textarea rows={6}
                      value={mypos.public_cert_pem || ''}
                      onChange={e => setMypos({ ...mypos, public_cert_pem: e.target.value })}
                      placeholder={mypos.public_cert_present ? '(ostavite prazno za zadržavanje postojećeg)' : '-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                      data-testid="mypos-public-cert"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-xs font-mono" />
          </div>

          <button type="submit" disabled={savingMypos}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-white font-medium disabled:opacity-50"
                  data-testid="mypos-save">
            {savingMypos ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Spremi MyPOS
          </button>
        </form>
      </section>

      {/* Email / SendGrid */}
      <section className="space-y-4 pt-6 border-t border-zinc-800">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-red-500" />
          <h2 className="text-lg font-bold">SendGrid email</h2>
          {email.api_key_configured ? (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-sm">Konfiguriran</span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-600 text-white px-2 py-0.5 rounded-sm">Mock način</span>
          )}
        </div>
        <p className="text-xs text-zinc-400 -mt-1">Admin može ovdje unijeti SendGrid API ključ. Bez ključa, email se zapisuje u `email_log` kolekciju kao MOCK.</p>

        <form onSubmit={saveEmail} className="space-y-4 max-w-2xl">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">SendGrid API Key {email.api_key_masked && <span className="text-[10px] text-emerald-400 ml-2">({email.api_key_masked})</span>}</label>
            <input type="password" value={email.api_key || ''}
                   onChange={e => setEmail({ ...email, api_key: e.target.value })}
                   placeholder={email.api_key_configured ? '(ostavite prazno za zadržavanje postojećeg)' : 'SG.xxxxxxxxxxxxxxxxxxxxxx'}
                   data-testid="email-api-key"
                   className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">From email</label>
              <input value={email.from_email || ''} onChange={e => setEmail({ ...email, from_email: e.target.value })}
                     placeholder="support@e-mina.eu"
                     data-testid="email-from-email"
                     className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">From name</label>
              <input value={email.from_name || ''} onChange={e => setEmail({ ...email, from_name: e.target.value })}
                     placeholder="e-mina.eu"
                     data-testid="email-from-name"
                     className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!email.mock_mode}
                   onChange={e => setEmail({ ...email, mock_mode: e.target.checked })}
                   data-testid="email-mock-mode" />
            <span>Mock mode (nemoj slati, samo zapiši u log)</span>
          </label>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={savingEmail}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-white font-medium disabled:opacity-50"
                    data-testid="email-save">
              {savingEmail ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Spremi email
            </button>
            <input type="email" value={testRecipient} onChange={e => setTestRecipient(e.target.value)}
                   placeholder="test@example.com"
                   data-testid="email-test-recipient"
                   className="flex-1 bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <button type="button" onClick={sendTestEmail} disabled={!testRecipient}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 rounded-md text-sm disabled:opacity-40"
                    data-testid="email-test-send">
              <Send size={14} /> Pošalji test
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
